import { useMemo } from 'react';
import type { Device, SleepData, Health, ScheduleType, EmployeeFollowup } from '../types';

type SleepStatusFilter = 'normal' | 'abnormal' | 'nodata';

interface SleepStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  statusFilter: SleepStatusFilter;
  devices: Device[];
  sleepDataMap: Map<string, SleepData>;
  healthDataMap: Map<string, Health[]>;
  deviceShiftMap: Map<string, ScheduleType>;
  followupMap: Map<string, EmployeeFollowup>;
  minSleepDuration: number;
  onOpenFollowup: (device: Device) => void;
  getFollowupBadgeColor: (followupType: string) => string;
}

export function SleepStatusModal({
  isOpen,
  onClose,
  statusFilter,
  devices,
  sleepDataMap,
  healthDataMap,
  deviceShiftMap,
  followupMap,
  minSleepDuration,
  onOpenFollowup,
  getFollowupBadgeColor,
}: SleepStatusModalProps) {
  // Helper functions
  const getStressLevelText = (value: number): string => {
    if (value === 0) return 'Relax';
    if (value <= 30) return 'Relaxed';
    if (value <= 50) return 'Low Stress';
    if (value < 75) return 'Medium Stress';
    return 'High Stress';
  };

  const getStressLevelColor = (value: number): string => {
    if (value === 0) return 'text-green-600';
    if (value <= 30) return 'text-green-600';
    if (value <= 50) return 'text-yellow-600';
    if (value < 75) return 'text-orange-600';
    return 'text-red-600';
  };

  const getSleepStatusText = (sleepTimeSeconds: number): string => {
    const minSleepInSeconds = minSleepDuration * 60;
    if (sleepTimeSeconds === 0) return 'No Sleep Data';
    if (sleepTimeSeconds >= minSleepInSeconds) return 'Normal Sleep';
    return 'Abnormal Sleep';
  };

  const getSleepStatusColor = (sleepTimeSeconds: number): string => {
    const minSleepInSeconds = minSleepDuration * 60;
    if (sleepTimeSeconds === 0) return 'text-white px-2 py-1 rounded bg-gray-800';
    if (sleepTimeSeconds >= minSleepInSeconds) return 'text-green-600';
    return 'text-red-600';
  };

  // Filter devices based on status
  const filteredDevices = useMemo(() => {
    const minSleepInSeconds = minSleepDuration * 60;
    
    return devices.filter(device => {
      // Exclude devices without group assignment
      if (!device.group_id || device.group_id === 'unassigned') {
        return false;
      }
      
      // Exclude devices with "off" shift or no shift
      const deviceShift = deviceShiftMap.get(device.id);
      if (!deviceShift || deviceShift === 'off') {
        return false;
      }
      
      const sleepData = sleepDataMap.get(device.id);
      const sleepTimeSeconds = sleepData?.sleepTime || 0;
      
      switch (statusFilter) {
        case 'normal':
          return sleepTimeSeconds > 0 && sleepTimeSeconds >= minSleepInSeconds;
        case 'abnormal':
          return sleepTimeSeconds > 0 && sleepTimeSeconds < minSleepInSeconds;
        case 'nodata':
          return sleepTimeSeconds === 0;
        default:
          return false;
      }
    });
  }, [devices, sleepDataMap, statusFilter, minSleepDuration, deviceShiftMap]);

  const getModalTitle = () => {
    switch (statusFilter) {
      case 'normal':
        return 'Normal Sleep';
      case 'abnormal':
        return 'Abnormal Sleep';
      case 'nodata':
        return 'No Data Sleep';
      default:
        return 'Sleep Status';
    }
  };

  const getModalColor = () => {
    switch (statusFilter) {
      case 'normal':
        return 'bg-green-800';
      case 'abnormal':
        return 'bg-red-600';
      case 'nodata':
        return 'bg-gray-800';
      default:
        return 'bg-gray-800';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl w-full h-full max-w-full max-h-full flex flex-col">
          {/* Header */}
          <div className={`${getModalColor()} px-6 py-4 flex items-center justify-between rounded-t-lg`}>
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold text-white">{getModalTitle()}</h2>
              <span className="bg-white bg-opacity-20 text-dark px-3 py-1 rounded-full text-sm font-semibold">
                {filteredDevices.length} devices
              </span>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-gray-500/50 rounded-full p-2 transition-colors"
              title="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto p-6">
            {filteredDevices.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
                <p className="text-gray-500 text-lg">No devices found with {getModalTitle()} status</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-max">
                  <thead>
                    <tr className="border-b-2 border-gray-300 bg-gray-50">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">ID Employee</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">Duty</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">Shift</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">Sleep Status</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">Start Time</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">End Time</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">Total Sleep</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">Sleep Quality</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">HR</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">SpO2</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">Stress</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">Action</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDevices.map((device, index) => {
                      const sleepData = sleepDataMap.get(device.id);
                      const healthData = healthDataMap.get(device.id) || [];
                      const sleepTimeSeconds = sleepData?.sleepTime || 0;
                      
                      return (
                        <tr 
                          key={device.id} 
                          className={`border-b border-gray-200 hover:bg-gray-50 transition-colors ${
                            index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                          }`}
                        >
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">{device.employee?.NIP || '-'}</td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              {device.employee?.Foto ? (
                                <img 
                                  src={device.employee.Foto} 
                                  alt={device.employee.Nama}
                                  className="w-10 h-10 rounded object-cover"
                                />
                              ) : (
                                <div className="w-10 h-10 rounded bg-gray-300 flex items-center justify-center">
                                  <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                  </svg>
                                </div>
                              )}
                              {device.employee?.Nama || device.name || 'Unnamed'}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">{device.employee?.Jabatan || '-'}</td>
                          <td className="px-4 py-3 text-sm whitespace-nowrap">
                            {deviceShiftMap.get(device.id) ? (
                              <span className={`inline-block px-2 py-1 text-xs rounded font-medium ${
                                deviceShiftMap.get(device.id) === 'fullday' ? 'bg-blue-100 text-blue-800' :
                                deviceShiftMap.get(device.id) === 'day' ? 'bg-yellow-100 text-yellow-800' :
                                deviceShiftMap.get(device.id) === 'night' ? 'bg-purple-100 text-purple-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {deviceShiftMap.get(device.id) === 'fullday' ? 'Full Day' :
                                 deviceShiftMap.get(device.id) === 'day' ? 'Day Shift' :
                                 deviceShiftMap.get(device.id) === 'night' ? 'Night Shift' :
                                 'Off'}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-xs">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm whitespace-nowrap">
                            <span className={`font-medium text-xs ${getSleepStatusColor(sleepTimeSeconds)}`}>
                              {getSleepStatusText(sleepTimeSeconds)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 text-center whitespace-nowrap">
                            {sleepData?.startTime ? (() => {
                              const date = new Date(sleepData.startTime);
                              const hours = date.getUTCHours().toString().padStart(2, '0');
                              const minutes = date.getUTCMinutes().toString().padStart(2, '0');
                              return `${hours}:${minutes}`;
                            })() : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 text-center whitespace-nowrap">
                            {sleepData?.endTime ? (() => {
                              const date = new Date(sleepData.endTime);
                              const hours = date.getUTCHours().toString().padStart(2, '0');
                              const minutes = date.getUTCMinutes().toString().padStart(2, '0');
                              return `${hours}:${minutes}`;
                            })() : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 font-semibold text-center whitespace-nowrap">
                            {sleepTimeSeconds > 0 ? (() => {
                              const hours = Math.floor(sleepTimeSeconds / 3600);
                              const mins = Math.floor((sleepTimeSeconds % 3600) / 60);
                              return `${hours}h ${mins}m`;
                            })() : 'No Data'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 text-center whitespace-nowrap">
                            {sleepData?.sleepQuality ? `${Math.round(sleepData.sleepQuality * 10000)}%` : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 text-center whitespace-nowrap">
                            {(() => {
                              const heartRateData = healthData.filter(h => h.data_type === 'heart_rate').map(h => h.string_val);
                              const avgHeartRate = heartRateData.length > 0 
                                ? Math.round(heartRateData.reduce((a, b) => a + b, 0) / heartRateData.length)
                                : 0;
                              return avgHeartRate > 0 ? `${avgHeartRate} bpm` : '-';
                            })()}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 text-center whitespace-nowrap">
                            {(() => {
                              const bloodOxygenData = healthData.filter(h => h.data_type === 'blood_oxygen').map(h => h.string_val);
                              const avgBloodOxygen = bloodOxygenData.length > 0
                                ? Math.round(bloodOxygenData.reduce((a, b) => a + b, 0) / bloodOxygenData.length)
                                : 0;
                              return avgBloodOxygen > 0 ? `${avgBloodOxygen}%` : '-';
                            })()}
                          </td>
                          <td className="px-4 py-3 text-sm text-center whitespace-nowrap">
                            {(() => {
                              const stressLevelData = healthData.filter(h => h.data_type === 'pressure').map(h => h.string_val);
                              const avgStressLevel = stressLevelData.length > 0
                                ? Math.round(stressLevelData.reduce((a, b) => a + b, 0) / stressLevelData.length)
                                : 0;
                              return (
                                <span className={`font-medium text-xs ${avgStressLevel > 0 ? getStressLevelColor(avgStressLevel) : 'text-gray-400'}`}>
                                  {avgStressLevel > 0 ? getStressLevelText(avgStressLevel) : '-'}
                                </span>
                              );
                            })()}
                          </td>
                          <td className="px-4 py-3 text-sm text-center whitespace-nowrap">
                            {device.idEmployee && followupMap.get(device.idEmployee) ? (
                              <button
                                onClick={() => onOpenFollowup(device)}
                                className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getFollowupBadgeColor(followupMap.get(device.idEmployee)!.followup_type)}`}
                                title={followupMap.get(device.idEmployee)!.notes || 'Click to edit'}
                              >
                                {followupMap.get(device.idEmployee)!.followup_type}
                              </button>
                            ) : device.idEmployee ? (
                              <button
                                onClick={() => onOpenFollowup(device)}
                                className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200"
                              >
                                + Follow Up
                              </button>
                            ) : (
                              <span className="text-gray-400 text-xs">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 whitespace-normal max-w-xs">
                            {device.idEmployee && followupMap.get(device.idEmployee)?.notes ? (
                              <span className="text-gray-700">{followupMap.get(device.idEmployee)!.notes}</span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 rounded-b-lg">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-600">
                Showing {filteredDevices.length} device{filteredDevices.length !== 1 ? 's' : ''} with {getModalTitle()} status
              </p>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
